import './underwriting.submission.underwriting-rules.section.scss';

import {
  Box,
  SvgIconTypeMap,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CircularLoader,
  ConvergeTooltip,
  ErrorPageLayout,
  FormContainerWithHeader,
  FormFieldContainer,
  StatusBadge,
  StyledConvergeFormTableCell,
  StyledConvergeFormTableContainer,
} from '../../../presentation';
import { ReactNode, useEffect, useMemo } from 'react';
import { SortOrder, SubmissionStatus } from '@converge/data';
import {
  StatusInfo,
  StatusTypeEnum,
  SubmissionSubStatus,
  TargetAreaType,
  UnderwritingRulesStatus,
  statusValueToStatusInfo,
  useShouldPoll,
  useStatusesQuery,
  useUnderwritingRulesQuery,
} from '@converge/graphql';
import { asCase, displayDateFromNow, displayFormatDate } from '@converge/utils';

import ClearIcon from '@mui/icons-material/Clear';
import { CustomPaletteColor } from '../../../theme/app.theme';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import OutboxIcon from '@mui/icons-material/Outbox';
import { QuestionMark } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { StatusesList } from '../../submission/submission-status-badge';
import { useAuthContext } from '../../auth';
import { useParams } from 'react-router-dom';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';

export interface IUnderwritingOverviewUnderwritingRulesSection {
  updateStatus:
    | {
        __typename?: 'SubscriptionNoticeDetails' | undefined;
        noticeType?: string | null | undefined;
        noticeArea?: string | null | undefined;
        noticeTask?: string | null | undefined;
        noticeName?: string | null | undefined;
        noticeValue?: string | null | undefined;
        noticeDagId?: string | null | undefined;
      }
    | null
    | undefined;
}
export const UnderwritingOverviewUnderwritingRulesSection = (
  props: IUnderwritingOverviewUnderwritingRulesSection
) => {
  const { updateStatus } = props;
  const {
    submission: { status, subStatus },
  } = useUnderwritingSubmissionStateContext();
  const { submissionUuid } = useParams<'submissionUuid'>();

  const { poll } = useShouldPoll();
  const { isUserIdle } = useAuthContext();

  const { info, data, loading, error, refetch } = useStatusesQuery({
    operationName: 'overviewUnderwritingRulesSection',
    sort: SortOrder.Asc,
    filters: {
      targetUuid: submissionUuid,
      targetArea: TargetAreaType.Submission,
      details: false,
      statusType: StatusTypeEnum.Processor,
    },
    poll: isUserIdle ? false : poll,
  });

  useEffect(() => {
    if (
      ((status === SubmissionStatus.Evaluation ||
        subStatus === SubmissionSubStatus.EvaluationEditable ||
        subStatus === SubmissionSubStatus.EvaluationNotEditable) &&
        updateStatus?.noticeType === 'PROCESSOR' &&
        updateStatus?.noticeValue === 'completed') ||
      (updateStatus?.noticeArea === 'Cyber Security Metrics' &&
        updateStatus?.noticeName === 'status' &&
        updateStatus?.noticeValue === 'completed')
    ) {
      refetch();
    }
  }, [subStatus, status, updateStatus, refetch]);

  const { unfinishedRulesStatuses } = useMemo(() => {
    const rulesStatus = info.statuses.filter(
      (status) => status.statusArea?.toLowerCase() === 'underwriting rules'
    );

    const rulesStatusInfo = rulesStatus.map((status) =>
      statusValueToStatusInfo(status.statusValue)
    );

    const unfinishedRulesStatuses = rulesStatusInfo.filter((info) => {
      return !info.finished;
    });

    return {
      rulesStatus,
      rulesStatusInfo,
      unfinishedRulesStatuses,
    };
  }, [info.statuses]);

  if (loading && !data) {
    return (
      <CircularLoader flexGrow={1} padding={5} style={{ color: ' #28c0a7' }} />
    );
  }

  if (error) {
    return (
      <ErrorPageLayout
        message={error.message}
        messageId="underwriting.overview.underwriting-rules.section.error"
      />
    );
  }

  const updatedStatus = info.statuses.map((status) => {
    if (
      status.statusArea === updateStatus?.noticeArea &&
      status.statusValue !== updateStatus?.noticeValue
    ) {
      return { ...status, statusValue: updateStatus?.noticeValue };
    }
    return status;
  });

  return (
    <div>
      <StatusesList
        statusAreaLabel="Process"
        statuses={
          updateStatus !== null || updateStatus !== undefined
            ? updatedStatus
            : info.statuses
        }
        poll={poll}
        showDagIdColumn
      />
      <br />
      <Box style={{ marginTop: '7px' }}>
        <FormContainerWithHeader title="Underwriting Rules">
          <FormFieldContainer>
            <UnderwritingRulesList
              poll={unfinishedRulesStatuses.length > 0}
              statusInfo={unfinishedRulesStatuses[0]}
              updateStatus={updateStatus}
            />
          </FormFieldContainer>
        </FormContainerWithHeader>
      </Box>
    </div>
  );
};

export const UnderwritingRulesList = ({
  poll = false,
  statusInfo,
  updateStatus,
}: {
  poll: boolean;
  statusInfo?: StatusInfo;
  updateStatus?:
    | {
        __typename?: 'SubscriptionNoticeDetails' | undefined;
        noticeType?: string | null | undefined;
        noticeArea?: string | null | undefined;
        noticeTask?: string | null | undefined;
        noticeName?: string | null | undefined;
        noticeValue?: string | null | undefined;
        noticeDagId?: string | null | undefined;
      }
    | null
    | undefined;
}) => {
  const { submissionUuid } = useParams<'submissionUuid'>();
  // const { isUserIdle } = useAuthContext();

  // const [setShouldRefetch] = useState<boolean>(false);

  const { underwritingRules, loading, error, refetch } =
    useUnderwritingRulesQuery(submissionUuid);

  useEffect(() => {
    if (
      (updateStatus !== null || updateStatus !== undefined) &&
      (updateStatus?.noticeValue === 'completed' ||
        updateStatus?.noticeValue === 'pending') &&
      updateStatus?.noticeArea === 'Underwriting Rules' &&
      updateStatus?.noticeDagId === 'SCORE_AND_RATE'
    ) {
      refetch();
    }
  }, [refetch, updateStatus]);

  if (loading) {
    return (
      <CircularLoader flexGrow={1} padding={5} style={{ color: ' #28c0a7' }} />
    );
  }

  if (error) {
    return (
      <ErrorPageLayout
        message={error.message}
        messageId="underwriting.overview.underwriting-rules.section.error"
      />
    );
  }

  return (
    <div>
      <StyledConvergeFormTableContainer>
        <Table
          className="underwriting-rule-container"
          aria-label="status"
          size="small"
        >
          <TableHead title="">
            <TableRow className="underwriting-rule-title">
              <StyledConvergeFormTableCell
                component="th"
                scope="row"
                className="table-header-overview-page"
              >
                Rule
              </StyledConvergeFormTableCell>
              <StyledConvergeFormTableCell
                component="th"
                scope="row"
                width="200px"
                className="table-header-overview-page"
              >
                Run date
              </StyledConvergeFormTableCell>
              <StyledConvergeFormTableCell
                className="table-header-overview-page"
                component="th"
                scope="row"
              ></StyledConvergeFormTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {underwritingRules.length > 0 ? (
              underwritingRules.map(({ reason, status, createdAt }, key) => {
                return (
                  <TableRow key={key}>
                    <StyledConvergeFormTableCell>
                      <Box
                        sx={{
                          margin: 1,
                          '& > *:first-child': {
                            marginTop: 0,
                          },
                          '& > *:last-child': {
                            marginBottom: 0,
                          },
                        }}
                      >
                        <ReactMarkdown>{reason ?? 'N/A'}</ReactMarkdown>
                      </Box>
                    </StyledConvergeFormTableCell>
                    <StyledConvergeFormTableCell>
                      <ConvergeTooltip
                        title={displayFormatDate(
                          createdAt,
                          displayFormatDate.DateFormatType
                            .USADateFormatWithTimeAt
                        )}
                      >
                        <span>
                          {displayDateFromNow(createdAt, {
                            ifOlderThanShowDate: true,
                            showTime: true,
                          })}
                        </span>
                      </ConvergeTooltip>
                    </StyledConvergeFormTableCell>
                    <StyledConvergeFormTableCell className="info-section">
                      <ConvergeTooltip title={asCase(status ?? 'N/A')}>
                        <StatusBadge
                          color={underwritingRulesStatusToColor(status)}
                          label={status ?? 'N/A'}
                          icon={<UnderwritingRulesStatusIcon status={status} />}
                        />
                      </ConvergeTooltip>
                    </StyledConvergeFormTableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <StyledConvergeFormTableCell
                  component="th"
                  scope="row"
                  colSpan={3}
                >
                  <Typography color="gray">
                    {!statusInfo
                      ? 'No result'
                      : statusInfo?.failed
                      ? `Underwriting rules failed to run`
                      : statusInfo?.completed
                      ? `All underwriting rules passed`
                      : statusInfo?.loading || statusInfo?.pending
                      ? `Underwriting rules are running...`
                      : `No underwriting rules found`}
                  </Typography>
                </StyledConvergeFormTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledConvergeFormTableContainer>
    </div>
  );
};

const underwritingRulesStatusToColor = (
  status: UnderwritingRulesStatus | null | undefined | string
): CustomPaletteColor => {
  switch (status) {
    case UnderwritingRulesStatus.Refer:
      return CustomPaletteColor.Warning;
    case UnderwritingRulesStatus.Decline:
      return CustomPaletteColor.Error;
    case UnderwritingRulesStatus.Info:
      return CustomPaletteColor.Blue;
    default:
      return CustomPaletteColor.DarkGray;
  }
};

const UnderwritingRulesStatusIcon = ({
  status,
  color = underwritingRulesStatusToColor(
    status
  ) as SvgIconTypeMap['props']['color'],
}: {
  status: UnderwritingRulesStatus | null | undefined | string;
  color?: SvgIconTypeMap['props']['color'];
}) => {
  const iconProps: SvgIconTypeMap['props'] = {
    color,
    fontSize: 'small',
    sx: {
      padding: '3px',
      display: 'flex',
      marginRight: '-5px',
      paddingLeft: '5px',
    },
  };

  let icon: ReactNode;

  switch (status) {
    case UnderwritingRulesStatus.Decline:
      icon = <ClearIcon {...iconProps} />;
      break;
    case UnderwritingRulesStatus.Info:
      icon = <InfoOutlined {...iconProps} />;
      break;
    case UnderwritingRulesStatus.Refer:
      icon = <OutboxIcon {...iconProps} />;
      break;
    default:
      icon = <QuestionMark {...iconProps} />;
      break;
  }

  return icon;
};

// export const PollingIndicator = ({
//   children,
//   poll,
//   pollIntervalInSeconds = config.pollIntervalsInSeconds.statuses,
// }: PropsWithChildren<{
//   poll: boolean;
//   pollIntervalInSeconds?: number;
// }>) => {
//   return (
//     <Stack flexDirection={'row'} alignItems={'center'} gap={2} flexGrow={1}>
//       <Stack flexGrow={1} flexDirection={'row'} alignItems={'center'}>
//         {children}
//       </Stack>
//       <Stack
//         flexDirection={'row'}
//         alignItems={'center'}
//         justifyContent={'center'}
//       >
//         <ConvergeTooltip
//           title={`Updating every ${pollIntervalInSeconds} second${
//             pollIntervalInSeconds === 1 ? '' : 's'
//           }`}
//           disabled={!poll}
//         >
//           <Sync
//             className="spin transition-opacity"
//             fontSize="small"
//             sx={{
//               opacity: poll ? 1 : 0,
//             }}
//           />
//         </ConvergeTooltip>
//       </Stack>
//     </Stack>
//   );
// };
